// client/src/supabaseClient.ts
//
// Lazy Supabase browser client. The `@supabase/ssr` SDK is ~970 KB
// uncompressed and is currently in the /dashboard initial HTML because
// AuthContext (reachable from every authenticated route via `useAuth()`)
// statically imported this module, which called `createBrowserClient(...)`
// at module load. Switching to a lazy getter means the SDK is loaded in a
// separate chunk the first time we actually need it - `getSession()`,
// `onAuthStateChange()`, `signOut()`, `refreshSession()`, etc. None of those
// run during the first paint of /dashboard.
//
// Pattern:
//   const supabase = await getSupabase();
//   await supabase.auth.getSession();
//
// We deliberately do NOT export the bare `supabase` instance any more.
// Tests that mocked `import { supabase } from '@/supabaseClient'` should now
// mock `getSupabase` instead (jest.mock('@/supabaseClient', () => ({
//   getSupabase: jest.fn(...)
// }))). DEL-771.

import type { createBrowserClient } from '@supabase/ssr';

type BrowserClient = Awaited<ReturnType<typeof createBrowserClient>>;

let _supabase: BrowserClient | null = null;
let _supabasePromise: Promise<BrowserClient> | null = null;

const SUPABASE_URL =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_URL) ||
  'http://localhost:54321';

const SUPABASE_ANON_KEY =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_ANON_KEY) ||
  'your-anon-key';

/**
 * Returns the singleton Supabase browser client, loading the `@supabase/ssr`
 * SDK on the first call. Subsequent calls resolve to the cached instance.
 * Safe to call from any code path - server-side calls (where `window` is
 * undefined) still create the client but the rest of the app does not use
 * the SDK there.
 */
export function getSupabase(): Promise<BrowserClient> {
  if (_supabase) return Promise.resolve(_supabase);
  if (_supabasePromise) return _supabasePromise;

  _supabasePromise = (async () => {
    const { createBrowserClient } = await import('@supabase/ssr');
    const client = createBrowserClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        flowType: 'pkce',
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
    _supabase = client;
    return client;
  })();

  return _supabasePromise;
}

/**
 * Test-only reset. Production callers should never need this; the singleton
 * lives for the lifetime of the page.
 */
export function __resetSupabaseForTests(): void {
  _supabase = null;
  _supabasePromise = null;
}